import type { CitationReferenceData, TokenReference } from 'services/reference-generator/types';
import { citationReviewStatus, isAiCreatedCitation } from './reviewAggregator';

/**
 * SCAL-8970 - the manual-citation add decision, extracted pure.
 *
 * DEFAULT = APPEND (keep both citations). Replace is a deliberate, explicit
 * user choice that soft-dismisses the cell's AI citation on the backend; it
 * must never happen implicitly, so the drawer only offers it when
 * `shouldOfferReplace` says the choice is real.
 */

export type ManualCitationMode = 'append' | 'replace';

export interface ManualCitationTarget {
  fieldName: string | undefined;
  documentReferenceId: number;
  tokenReference: TokenReference;
}

// The live entry a new citation merges into. A dismissed entry is hidden and Save treats a saved id as not an add,
// so new text goes into a new entry the backend can adopt.
const sameDocEntry = (refs: CitationReferenceData[], fieldName: string | undefined, documentReferenceId: number) =>
  refs.find(
    ref =>
      ref.field_name === fieldName &&
      ref.document_reference === documentReferenceId &&
      citationReviewStatus(ref) !== 'dismissed'
  );

/**
 * Whether the add should prompt for Append vs Replace: only when the pushed
 * citation would CREATE a new entry (no same-document entry to merge into) and
 * the field already carries a visible (non-dismissed) AI-created citation.
 */
export const shouldOfferReplace = (
  existingRefs: CitationReferenceData[] | undefined,
  fieldName: string | undefined,
  documentReferenceId: number
): boolean => {
  const refs = existingRefs ?? [];
  return (
    !sameDocEntry(refs, fieldName, documentReferenceId) &&
    refs.some(
      ref =>
        ref.field_name === fieldName && isAiCreatedCitation(ref) && citationReviewStatus(ref) !== 'dismissed'
    )
  );
};

/**
 * Add a pushed citation to a cell: merge the token into the field's live same-document entry, otherwise append a
 * new human-created entry. In 'replace' mode the new entry asks the save to dismiss the field's other AI citations,
 * which the backend does only when it creates or adopts that row, so a re-sent flag is harmless.
 */
export const addManualCitation = (
  existingRefs: CitationReferenceData[] | undefined,
  { fieldName, documentReferenceId, tokenReference }: ManualCitationTarget,
  mode: ManualCitationMode
): CitationReferenceData[] => {
  const refs = existingRefs ?? [];
  const existing = sameDocEntry(refs, fieldName, documentReferenceId);
  if (existing) {
    existing.tokenReferences.push(tokenReference);
    existing.is_accepted = true;
    return refs;
  }
  return [
    ...refs,
    {
      document_reference: documentReferenceId,
      field_name: fieldName ?? '',
      is_accepted: true,
      created_source: 'human',
      tokenReferences: [tokenReference],
      ...(mode === 'replace' ? { replace: true } : {}),
    },
  ];
};
